'use client';

import React from 'react';
import {InstructorDetails} from '@udemy-v2/browse-course';
import styles from './instructor-display.module.scss';
import classNames from 'classnames';
import {useI18n, LocalizedHtml} from '@udemy-v2/i18n';
import {Button} from '@udemy-v2/react-core-components';
import {generateInstructorAnchorHash} from '../instructor-module/utils/instructor-utils';
import {Icon} from '@udemy-v2/icons';
import {useInstructorCommunityBannerInfo} from '@/lib/hooks/use-instructor-community-banner-info';
import {useInstructorDetails} from '@/lib/hooks/use-instructor-details';
import {CommunityPoweredBadge} from '../instructor-community/community-powered-badge';
import {CommunityPlusRibbon} from '../instructor-community/community-plus-ribbon';

interface InstructorDisplayProps {
    instructors: InstructorDetails[];
    isAGwGCourse?: boolean;
}

/**
 * Displays course instructors with links to their profiles
 *
 * @example
 * ```jsx
 * <InstructorDisplay instructors={course.instructors} />
 * ```
 */
export function InstructorDisplay({instructors, isAGwGCourse = false}: InstructorDisplayProps) {
    const {gettext, interpolate} = useI18n();
    const firstInstructor = instructors?.[0];
    const {hasCommunity} = useInstructorCommunityBannerInfo(firstInstructor?.id);
    const instructorDetails = useInstructorDetails();

    if (!instructors?.length) return null;

    const showCommunityPoweredBadge = !isAGwGCourse && hasCommunity;
    // The GraphQL course query only ever has a 50x50 thumbnail (images.small). The taught-courses
    // REST data (fetched by CourseWithInstructorDetailsProvider) has a real higher-res image, so
    // prefer it once it's loaded, falling back to the low-res thumbnail until then.
    const firstInstructorDetails = instructorDetails.find(
        (instructor) => instructor.id === firstInstructor?.id,
    );
    const firstInstructorImageUrl =
        firstInstructorDetails?.images.large ?? firstInstructor?.images.small;

    /**
     * Updates the instructor link to include the current URL's query parameters
     * to avoid a full page refresh when a coupon is applied
     */
    const handleOnInstructorNameClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = event.target as HTMLElement;
        const parentElement = target.parentElement as HTMLAnchorElement | null;
        const position = parentElement?.dataset?.position || '1';
        const id = generateInstructorAnchorHash(parseInt(position, 10) - 1);

        if (parentElement) {
            parentElement.href = `${window.location.search}${id}`;
        }
    };

    const renderInstructorHeaderImage = () => {
        if (!showCommunityPoweredBadge || !firstInstructorImageUrl) {
            return null;
        }

        return (
            <div
                className={styles['instructor-header-leading']}
                data-purpose="instructor-header-image"
            >
                <div className={styles['instructor-header-image-container']}>
                    <CommunityPoweredBadge
                        imageUrl={firstInstructorImageUrl}
                        displayName={firstInstructor.name}
                        size="sm"
                    />
                </div>
                <CommunityPlusRibbon />
            </div>
        );
    };

    /**
     * Renders instructor names as buttons with proper comma separation
     */
    const renderInstructorNames = () => {
        // Component to render instructor names with commas
        const Names = () => {
            if (isAGwGCourse) {
                return (
                    <Button
                        componentClass="a"
                        udStyle="link"
                        className={classNames('ud-text-sm', styles['instructor-link'])}
                        onClick={handleOnInstructorNameClick}
                        href={generateInstructorAnchorHash(0)}
                    >
                        Google
                    </Button>
                );
            }
            const instructorButtons = instructors.map((instructor, i) => (
                <Button
                    componentClass="a"
                    udStyle="link"
                    key={instructor.id}
                    className={classNames('ud-text-sm', styles['instructor-link'])}
                    onClick={handleOnInstructorNameClick}
                    data-position={i + 1}
                    href={instructor.url || generateInstructorAnchorHash(i)}
                >
                    {instructor.name}
                </Button>
            ));

            // Combine buttons with commas between them using a safer approach for TypeScript
            return instructorButtons.reduce<React.ReactNode[]>((acc, curr, index) => {
                if (index === 0) {
                    return [curr];
                }
                return [...acc, ', ', curr];
            }, []);
        };

        return (
            <span
                className={classNames(
                    styles['instructor-container'],
                    showCommunityPoweredBadge && styles.names,
                )}
            >
                {isAGwGCourse && (
                    <Icon
                        glyph="google"
                        size="xsmall"
                        label={false}
                        className={styles['google-icon']}
                    />
                )}
                <LocalizedHtml
                    html={interpolate(gettext('<span class="created-by">Created by</span>'), true)}
                    interpolate={{
                        'created-by': <span className={styles['created-by']} />,
                    }}
                />
                <Names />
            </span>
        );
    };

    if (!showCommunityPoweredBadge) {
        return renderInstructorNames();
    }

    return (
        <div className={styles['with-community-badge']} data-purpose="instructor-name-top">
            {renderInstructorHeaderImage()}
            {renderInstructorNames()}
        </div>
    );
}
